import {useDeepMemo} from '@wandb/weave/hookUtils';
import {MutableRefObject, useCallback, useMemo, useRef} from 'react';

import {
  RunSelectorRunsQueryVariables,
  useRunSelectorRunsQuery,
} from '../../../generated/graphql';
import {propagateErrorsContext} from '../../../util/errors';
import * as Filters from '../../../util/filters';
import {usePinnedRunFilters} from '../../../util/hooks/usePinnedRunsFilters';
import {isNetworkRequestSettled} from '../../../util/isNetworkRequestSettled';
import * as Run from '../../../util/runs';
import {RunSelectorRunsPartialVars} from '../types';

type DeltaVariables = Pick<
  RunSelectorRunsQueryVariables,
  'currentRuns' | 'lastUpdated'
>;

export type UseRunSelectorRunsVariablesArgs = {
  /** Determines how many runs will be returned on initial load and when loading more runs */
  limit: number;
  /** Variables that will be used to compute the full set of query variables */
  partialVariables: RunSelectorRunsPartialVars;
  /** If true, filters will be stricter so only selected (visualized) runs are returned */
  shouldOnlyShowSelected?: boolean;
};

export type UseRunSelectorRunsQueryArgs = {
  variables?: RunSelectorRunsQueryVariables;
  /** If true, will not query for runs */
  skip?: boolean;
  deltaVariablesRef: MutableRefObject<DeltaVariables>;
};

export const useVariables = ({
  limit,
  partialVariables,
  shouldOnlyShowSelected,
}: UseRunSelectorRunsVariablesArgs) => {
  // Track these values so the backend knows what to compute deltas against.
  // Must use ref because we don't want to trigger a new request every time
  // these values are updated; instead, we just want to make sure they're
  // correctly populated when a request is made.
  const deltaVariablesRef = useRef<DeltaVariables>({
    currentRuns: [],
    lastUpdated: new Date(0).toISOString(),
  });

  const {
    projectInternalId,
    filters: baseFilters,
    selections,
    grouping,
    pinnedRunIds = [],
    baselineRunId,
    ...passThroughVars
  } = partialVariables;

  // passThroughVars needs to be memoized by value because it is recreated on every render
  // due to the spread operator
  const passThroughVarsMemoized = useDeepMemo(passThroughVars);

  // hack! baseFilters is changing on eyeball click due to a recent cache change.
  // this is a temporary fix to prevent the erroneous shimmer when baseFilters are
  // value-equal but erroneously not ref equal. TODO: remove once cache change
  // is fixed.
  const baseFiltersMemoized = useDeepMemo(baseFilters);

  const {excludedIDsFilter, pinnedRunsFilter} = usePinnedRunFilters(
    pinnedRunIds,
    baselineRunId
  );

  const filters = useMemo(() => {
    const filters =
      shouldOnlyShowSelected && selections != null
        ? Filters.And([baseFiltersMemoized, selections])
        : baseFiltersMemoized;

    if (excludedIDsFilter) {
      return Filters.And([filters, excludedIDsFilter]);
    }
    return filters;
  }, [
    shouldOnlyShowSelected,
    selections,
    baseFiltersMemoized,
    excludedIDsFilter,
  ]);

  const variables = useMemo<RunSelectorRunsQueryVariables | undefined>(() => {
    if (!projectInternalId) {
      return undefined;
    }

    const groupKeys = grouping?.map(Run.keyToServerPath) ?? [];
    return {
      // project identifier
      projectInternalId,

      // delta vars
      currentRuns: deltaVariablesRef.current.currentRuns,
      // fresh lastUpdated time ensures correct results on query change
      // it should only be set to something else on polled requests
      lastUpdated: new Date(0).toISOString(),

      // logical vars
      ...passThroughVarsMemoized,
      filters: JSON.stringify(Filters.toMongo(filters)),
      groupKeys,
      pinnedRunsFilter: JSON.stringify(Filters.toMongo(pinnedRunsFilter)),
      limit,
    };
  }, [
    projectInternalId,
    grouping,
    passThroughVarsMemoized,
    filters,
    pinnedRunsFilter,
    limit,
  ]);

  return {variables, deltaVariablesRef};
};

export const useRunSelectorRunsDataQuery = ({
  variables,
  skip,
  deltaVariablesRef,
}: UseRunSelectorRunsQueryArgs) => {
  const queryResults = useRunSelectorRunsQuery({
    context: propagateErrorsContext(),
    fetchPolicy: 'no-cache',
    skip: skip || !variables,
    variables,
  });

  const refetch = useCallback(() => {
    return queryResults.refetch({
      ...queryResults.variables,
      ...deltaVariablesRef.current,
    });
  }, [deltaVariablesRef, queryResults]);

  const isLoadingRuns =
    !skip && !isNetworkRequestSettled(queryResults.networkStatus);

  return {
    data: queryResults.data,
    isLoadingRuns,
    refetch,
  };
};
